/**
 * Pinia store for live PromoStandards inventory (issue #5).
 *
 * Backs the Live Inventory card on the Product editor's Supplier & SKU
 * panel. The backend route lives at:
 *
 *   GET    /api/supplier_integrations/promostandards/inventory
 *               ?supplier=<sanmar>&sku=<sku>
 *   DELETE /api/supplier_integrations/promostandards/inventory
 *               ?supplier=<sanmar>&sku=<sku>
 *
 * GET returns cached-or-live data; DELETE invalidates the 30-minute
 * Redis TTL cache AND returns a fresh fetch (the "Refresh" button's
 * contract in one round trip).
 *
 * State shape:
 *
 *   state.byKey -- map from `<supplier>:<sku>` to the per-SKU record:
 *     {
 *       loading: false,
 *       error: null | { message, status, code, retryable },
 *       data: null | { supplier, sku, queried_at, stale, colors: [...] },
 *     }
 *
 * Why a keyed map: a single product editor renders ONE live-inventory
 * card today, but a future "compare across SKUs" / supplier-side-by-side
 * surface gets the same cache+keying for free. Using a single ref would
 * make that change require a store refactor.
 *
 * Why a store at all (vs a composable): keeps a 30-minute lifetime
 * shared across components (e.g. if a sibling panel pre-fetches the
 * SKU) -- the wrapping component just calls ensure(...) and renders.
 */
import { defineStore } from 'pinia'
import { reactive } from 'vue'
import api from '@/services/api'

const ROUTE = '/supplier_integrations/promostandards/inventory'

// `styleId` is the product's persisted supplier style id (S&S styleID).
// One style NAME can name several S&S styles ("5200"), so a pinned style is
// its own entry: its stock must never be served for another style.
function makeKey(supplier, sku, styleId = null) {
  const base = `${(supplier || '').toLowerCase()}:${sku}`
  return styleId == null ? base : `${base}#${styleId}`
}

function emptyRecord() {
  return { loading: false, error: null, data: null }
}

// Error codes the backend uses for a 503 on this route. A parse failure
// on OUR side is deterministic -- retrying provably cannot help -- so the
// panel must not offer a Retry button for it (GH #3320). Anything we do
// not recognise is treated as retryable, which is the cautious default:
// a spurious Retry button is a far smaller harm than hiding a real one.
const NON_RETRYABLE_ERROR_CODES = new Set(['inventory_response_unreadable'])

function _errorRecord(err, fallbackMessage) {
  const code = err?.response?.data?.error ?? null
  return {
    message:
      err?.response?.data?.message || err?.message || fallbackMessage,
    status: err?.response?.status ?? null,
    code,
    retryable: !NON_RETRYABLE_ERROR_CODES.has(code),
  }
}

export const usePromostandardsInventoryStore = defineStore(
  'promostandardsInventory',
  () => {
    // Reactive map -- Pinia tracks each per-key record's mutations
    // because reactive() makes nested fields reactive too.
    const byKey = reactive({})

    function _ensureRecord(supplier, sku, styleId = null) {
      const key = makeKey(supplier, sku, styleId)
      if (!byKey[key]) {
        byKey[key] = emptyRecord()
      }
      return byKey[key]
    }

    function get(supplier, sku, styleId = null) {
      return byKey[makeKey(supplier, sku, styleId)] ?? emptyRecord()
    }

    /**
     * Build the request params for a fetch/refresh call.
     *
     * Scope filter (issue: scope Live Inventory to selected colors):
     *   - opts.scope = 'selected' | 'all' (default 'selected') —
     *     when 'selected' AND a campaign_product_id is supplied, the
     *     backend filters response.colors to the CP's selected_color_skus.
     *     'all' bypasses the filter so the user can see the full
     *     SanMar catalog (e.g. to add new colors).
     *   - opts.campaignProductId — required for scope='selected' to
     *     have any effect; when absent, backend falls back to full.
     *   - opts.styleId — the product's persisted supplier style id; sent
     *     as `style_id` so S&S answers for THAT style, not the name.
     *
     * Unknown params are simply omitted so callers from existing
     * call-sites continue to send only supplier+sku.
     */
    function _buildParams(supplier, sku, opts = {}) {
      const params = { supplier, sku }
      if (opts.scope) {
        params.scope = opts.scope
      }
      if (opts.campaignProductId != null) {
        params.campaign_product_id = opts.campaignProductId
      }
      if (opts.styleId != null) {
        params.style_id = opts.styleId
      }
      return params
    }

    /**
     * Fetch inventory. If a cached payload is already in state and
     * ``force`` is false, returns immediately. The backend has its
     * own 30-min cache; this store-level guard avoids a duplicate
     * round-trip when a sibling component mounted right before.
     *
     * Scope-aware re-fetch: when ``scope`` changes between calls for
     * the same (supplier, sku), the cached record is treated as
     * stale and a fresh fetch is issued — different scopes return
     * different color sets, so reusing the cache would surface the
     * wrong filtered view.
     */
    async function fetch(supplier, sku, opts = {}) {
      const { force = false, scope, campaignProductId, styleId = null } = opts
      const record = _ensureRecord(supplier, sku, styleId)
      const scopeChanged = scope != null && record._scope !== scope
      if (record.data && !force && !scopeChanged) return record.data
      if (record.loading) return record.data
      record.loading = true
      record.error = null
      try {
        const { data } = await api.get(ROUTE, {
          params: _buildParams(supplier, sku, { scope, campaignProductId, styleId }),
        })
        record.data = data
        record._scope = scope ?? null
        return data
      } catch (err) {
        // Surface either the backend's structured ``message`` or a
        // generic fallback. Don't clobber the previous ``data`` --
        // the panel may want to show stale-from-store on error.
        record.error = _errorRecord(err, 'Live inventory fetch failed')
        return record.data
      } finally {
        record.loading = false
      }
    }

    /**
     * Force-refresh: DELETE clears the backend cache and returns a
     * fresh fetch in one round trip. Replaces the per-key data.
     *
     * Honours the same scope contract as fetch() — when the user is
     * viewing scope=selected, refresh re-fetches with the same scope so
     * the visible table doesn't expand to the full catalog on refresh.
     */
    async function refresh(supplier, sku, opts = {}) {
      const { scope, campaignProductId, styleId = null } = opts
      const record = _ensureRecord(supplier, sku, styleId)
      record.loading = true
      record.error = null
      try {
        const { data } = await api.delete(ROUTE, {
          params: _buildParams(supplier, sku, { scope, campaignProductId, styleId }),
        })
        record.data = data
        record._scope = scope ?? null
        return data
      } catch (err) {
        record.error = _errorRecord(err, 'Live inventory refresh failed')
        return record.data
      } finally {
        record.loading = false
      }
    }

    /** Drop a single key (e.g. on SKU swap to clear the panel). */
    function clear(supplier, sku, styleId = null) {
      delete byKey[makeKey(supplier, sku, styleId)]
    }

    /** Drop everything (logout / impersonation-stop). */
    function clearAll() {
      for (const k of Object.keys(byKey)) {
        delete byKey[k]
      }
    }

    return {
      byKey,
      get,
      fetch,
      refresh,
      clear,
      clearAll,
    }
  },
)
